/* LocationChangesPage.js
   Shows before/after diff and an Approve button for the latest change
   Enhanced to show organization changes
*/
import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withStyles } from '@mui/styles';
import Button from '@mui/material/Button';
import { StoreUtil, DateUtil } from 'doctivity-shared/utils';
import { DynamicTable } from 'components';
import { LocationsTabs } from './LocationsTabs';
import { approveLocation } from 'store/actions/locationsActions';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { Typography, FormControlLabel, Switch } from '@mui/material';
import { queryActivityLogLocationUpdates } from '../store/actions/activityLogsActions';


const DIFF_FIELDS = [
  'name',
  'address1',
  'address2',
  'city',
  'state',
  'postal_code',
  'phone',
  'fax',
  'email',
  'organization_id'  // Added to track organization changes
];

/* ------------------------------------------------------------------ */
/*  Helper: render block of field values, changing ones in red      */
/* ------------------------------------------------------------------ */
const nbsp = '\u00A0';                // reusable non-breaking space

const renderFieldBlock = (values, changedSet, organizationData = null, organizationLookup = {}) => (
  <div>
    {DIFF_FIELDS.map((field) => {
      let val = values[field] ?? '';
      
      // Special handling for organization_id to show name instead of ID
      if (field === 'organization_id') {
        if (val && organizationData) {
          // Use embedded organization data if available
          val = organizationData.name || `Organization ID: ${val}`;
        } else if (val && organizationLookup[val]) {
          // Use lookup table if available
          val = organizationLookup[val].name || `Organization ID: ${val}`;
        } else if (val) {
          val = `Organization ID: ${val}`;
        } else {
          val = '(No Organization)';
        }
      }
      
      const showSpacer = val === '' && changedSet.has(field);

      return (
        <div
          key={field}
          style={{ color: changedSet.has(field) ? 'red' : undefined }}
        >
          {showSpacer ? nbsp : val}
        </div>
      );
    })}
  </div>
);

const columns = [
  {
    label : 'Before',
    key   : 'before',
    style : { width: '30%', verticalAlign: 'top' },
    format: (_ignored, row) => renderFieldBlock(row.before, row.changed, row.beforeOrganization, row.organizationLookup),
  },
  {
    label : 'After',
    key   : 'after',
    style : { width: '30%', verticalAlign: 'top' },
    format: (_ignored, row) => renderFieldBlock(row.after, row.changed, row.afterOrganization, row.organizationLookup),
  },
  {
    label : 'Date & Time',
    key   : 'created_at',
    format: DateUtil.formatDateTimeFromDB,
    style : { width: '13%' },
  },
  {
    label : 'Client',
    key   : 'selected_client.name',
    style : { width: '7%' },
  },
  {
    label : 'User',
    key   : 'user_id',
    format: (_id, row) =>
      row.user ? `${row.user.first_name} ${row.user.last_name}` : '',
  },
  {
    label : 'Approve',
    key   : 'approve',
    style : { width: '8%' },
    format: (_ignored, row, onApprove) =>
      row.isLatest ? (
        <Button
          variant='contained'
          color='primary'
          size='small'
          onClick={(e) => {
          e.preventDefault();     // ← stop the link
          e.stopPropagation();    // ← stop the row click handler
            onApprove(row);
          }}
        >
          Approve
        </Button>
      ) : null,
    sortable   : false,
    showFilter : false,
  },
];


const styles = (theme) => ({
  container: {
    padding: theme.spacing(3),
    '& table': {
      tableLayout: 'fixed',
    },
  },
  tableHeaderContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  spacer: {
    flex: 1,
  },
  switchLabel: {
    fontSize: 12,
    fontWeight: 200,
  }
});

class LocationChangesPage extends React.Component {

  state = {
    pendingApproal: false,
    filterClient: false,
    query: {
      limit: 25,
      offset: 0,
    },
  }
  
  /* ------------------ data fetch --------------------------------- */
  componentDidMount() {
    if (StoreUtil.needsLoadNoCache(this.props.activityLogsTable)) {
      this.fetchData();
    }
  }
  
  fetchData = () => {
    const { filterClient, query } = this.state;
    const { clientId } = this.props;
    
    // If filtering by client, pass the clientId, otherwise pass null/undefined for all clients
    const selectedClient = filterClient ? clientId : null;
    
    this.props.dispatch(queryActivityLogLocationUpdates(
      selectedClient, 
      query.limit || 25, 
      query.offset || 0
    ));
  };

  requestApprove = (row) => {
    this.setState({ pendingApproval: row });
  };

  confirmApprove = () => {
    const { pendingApproval } = this.state;
    if (!pendingApproval) return;

    const locationId = getLocationId(pendingApproval);
    if (!locationId) {
      console.error('Cannot approve: location id missing');
      this.cancelApprove();
      return;
    }

    this.props.dispatch(approveLocation(locationId));  // PATCH /Locations/:id
    setTimeout(this.fetchData, 500); 
    this.setState({ pendingApproval: null }, this.fetchData);

    /* 1. fire-and-forget API request */
  };
  
  cancelApprove = () => {
    this.setState({ pendingApproval: null });
  };

  handleApprove = (row) => {
    this.props.dispatch(approveLocation(row.object_id)); // fire-and-forget
  };

  onFilterClient = (event) => {
    this.setState({
      filterClient: event.target.checked,
    }); // No need to refetch data - filtering happens in render
  };

  inflateRows = (loaded) => {
    if (!loaded?.data) return loaded;

    const rawRows = Array.isArray(loaded.data)
      ? loaded.data
      : loaded.data.rows || [];

    const seen = new Set();

    // Build organization lookup from all embedded organization data
    const organizationLookup = {};
    rawRows.forEach((r) => {
      try {
        const ctx = JSON.parse(r.context || '{}');
        const current = ctx.data ?? {};
        const previous = ctx.previousData ?? {};
        
        // Collect organization data from current state
        if (current.organization && current.organization.id) {
          organizationLookup[current.organization.id] = current.organization;
        }
        
        // Collect organization data from previous state (if available)
        if (previous.organization && previous.organization.id) {
          organizationLookup[previous.organization.id] = previous.organization;
        }
      } catch {}
    });

    let processedRows = rawRows.map((r) => {
      /* -------- parse JSON and detect "new" ----------------------- */
      let ctx = {};
      try { ctx = JSON.parse(r.context || '{}'); } catch {}
      const current  = ctx.data         ?? {};
      const previous = ctx.previousData ?? {};

      const isNew = !ctx.previousData || Object.keys(ctx.previousData).length === 0;

      /* -------- compute diff -------------------------------------- */
      const before  = {};
      const after   = {};
      const changed = new Set();
      DIFF_FIELDS.forEach((f) => {
        const oldVal = previous[f] ?? '';
        const newVal = current[f]  ?? '';
        before[f] = oldVal;
        after[f]  = newVal;
        if (oldVal !== newVal) changed.add(f);
      });

      /* -------- extract organization data ------------------------- */
      const beforeOrganization = previous.organization || null;
      const afterOrganization = current.organization || null;

      /* -------- latest row per object_id -------------------------- */
      const isLatest = !seen.has(r.object_id);
      seen.add(r.object_id);

      return { 
        ...r, 
        before, 
        after, 
        changed, 
        isLatest, 
        isNew,
        beforeOrganization,
        afterOrganization,
        organizationLookup  // Pass lookup table to each row
      };
    });

    // Frontend filtering by client if enabled
    console.log('inflateRows called - filterClient:', this.state.filterClient, 'clientId:', this.props.clientId);
    console.log('Total rows before filtering:', processedRows.length);
    
    if (this.state.filterClient && this.props.clientId) {
      const beforeCount = processedRows.length;
      processedRows = processedRows.filter((row) => {
        const matches = row.selected_client?.id === this.props.clientId;
        console.log(`Row ${row.id}: selected_client.id=${row.selected_client?.id}, clientId=${this.props.clientId}, matches=${matches}`);
        return matches;
      });
      console.log(`Filtered from ${beforeCount} to ${processedRows.length} rows`);
    } else {
      console.log('No filtering applied - either filter is off or no clientId');
    }

    /* -------- return in the same structure we received ----------- */
    if (Array.isArray(loaded.data)) {
      return { ...loaded, data: processedRows };
    }

    return {
      ...loaded,
      data: { ...loaded.data, rows: processedRows, count: processedRows.length },
    };
  };


  render() {
    const { activityLogsTable, classes } = this.props;
    const { pendingApproval, query, filterClient } = this.state;
    
    const tableData = this.inflateRows(activityLogsTable);

    /* Inject onApprove into column formatter */
    const columnsWithApprove = columns.map((col) =>
      col.key === 'approve'
        ? { ...col, format: (val, row) => col.format(val, row, this.requestApprove) }
        : col
    );

    return (
      <>
        <LocationsTabs tab='location_changes' />
        <div className={classes.container}>
          <DynamicTable
            header={
              <div className={classes.tableHeaderContainer}>
                <Typography
                  component='h2'
                  variant='h6'
                  color='primary'
                  gutterBottom
                >
                  Location Changes
                </Typography>
                <div className={classes.spacer} />
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={filterClient} 
                        onChange={this.onFilterClient} 
                      />
                    }
                    label={
                      <Typography className={classes.switchLabel}>
                        Filter by Selected Client
                      </Typography>
                    }
                  />
                </div>
                <div className={classes.spacer} />
              </div>
            }
            columns={columnsWithApprove}
            data={tableData}
            query={query}
            showFilters={false}
            onQueryChange={this.onQueryChange}
            getRowHref={(row) => { const id = getLocationId(row); return id ? `/locations/${id}` : undefined;  }}            
            
          />
        </div>
        {/* -------- confirmation dialog --------------------------- */}
        <Dialog open={Boolean(pendingApproval)} onClose={this.cancelApprove}>
          <DialogTitle>Approve Location</DialogTitle>
          <DialogContent dividers>
            <Typography variant='body1'>
              Are you sure you want to mark the latest changes for&nbsp;
              <strong>{pendingApproval?.object_id}</strong>&nbsp;as approved?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.cancelApprove}>Cancel</Button>
            <Button onClick={this.confirmApprove} variant='contained' color='primary'>
              Approve
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  onQueryChange = (nextQuery) =>
    this.setState({ query: nextQuery }, this.fetchData);
}

/* ------------------------------------------------------------------ */
/* Redux + PropTypes                                                  */
LocationChangesPage.propTypes = {
  dispatch          : PropTypes.func.isRequired,
  classes           : PropTypes.object.isRequired,
  clientId          : PropTypes.number.isRequired,
  activityLogsTable : PropTypes.object.isRequired,
  userId            : PropTypes.number.isRequired,
};

const mapStateToProps = (state) => ({
  userId:   state.user?.id ?? null,
  clientId: state.app.selectedClient,
  activityLogsTable:
  state.activityLogs.table ?? StoreUtil.createNewLoadedData(),
});

function getLocationId(row) {
  if (row.object_id)               return row.object_id;
  if (row.location?.id)            return row.location.id;

  // brand-new insert → id is only in the context payload
  try {
    const ctx = JSON.parse(row.context || '{}');
    return ctx.data?.id ?? null;
  } catch {
    return null;                   // malformed JSON – give up
  }
}


const styled = withStyles(styles)(LocationChangesPage);
const connected = connect(mapStateToProps)(styled);
export { connected as LocationChangesPage };